import { z } from "zod"

/**
 * Liquid `image` object as it appears in `{{ variant | json }}` / variant arrays.
 * Field names and presence vary; extra keys are preserved via passthrough.
 */
export const LiquidImageSchema = z
  .object({
    alt: z.string().nullable().optional(),
    width: z.number().nullable().optional(),
    height: z.number().nullable().optional(),
    src: z.string().optional(),
  })
  .passthrough()
  .nullable()
  .optional()

/**
 * Product variant as serialized from Liquid (e.g. `all_products[x].variants | json`).
 * Differs from Admin GraphQL `ProductVariant` (see `shopify/admin/variant.ts`).
 *
 * @see https://shopify.dev/docs/api/liquid/objects/variant
 */
export const LiquidProductVariantSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    title: z.string(),
    available: z.boolean().optional(),
    sku: z.string().nullable().optional(),
    barcode: z.string().nullable().optional(),
    price: z.union([z.string(), z.number()]).optional(),
    compare_at_price: z.union([z.string(), z.number()]).nullable().optional(),
    featured_image: LiquidImageSchema,
    image: LiquidImageSchema,
    option1: z.string().nullable().optional(),
    option2: z.string().nullable().optional(),
    option3: z.string().nullable().optional(),
    options: z.array(z.unknown()).optional(),
    requires_shipping: z.boolean().optional(),
    taxable: z.boolean().optional(),
    weight: z.number().nullable().optional(),
    weight_unit: z.string().optional(),
    weight_in_unit: z.number().nullable().optional(),
    url: z.unknown().optional(),
    inventory_quantity: z.number().optional(),
    inventory_policy: z.string().optional(),
    inventory_management: z.string().nullable().optional(),
    metafields: z.unknown().optional(),
  })
  .passthrough()

export type LiquidProductVariant = z.infer<typeof LiquidProductVariantSchema>
